import React, { useState, useCallback, useEffect } from 'react';
import { FiArrowLeft, FiArrowRight, FiAlertCircle, FiCheck } from 'react-icons/fi';

interface CsvRow {
  [key: string]: string;
}

interface RowError {
  row: number;
  reason: string;
  data: any;
}

interface PreviewStepProps {
  rows: CsvRow[];
  mapping: Record<string, string>;
  onComplete: (errors: RowError[], duplicates: number[]) => void;
  onBack: () => void;
  onClose: () => void;
}

export const PreviewStep: React.FC<PreviewStepProps> = ({
  rows,
  mapping,
  onComplete,
  onBack,
  onClose
}) => {
  const [errors, setErrors] = useState<RowError[]>([]);
  const [duplicates, setDuplicates] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const processData = async () => {
      setIsProcessing(true);
      
      const newErrors: RowError[] = [];
      const newDuplicates: number[] = [];

      // Validate each row
      rows.forEach((row, index) => {
        const rowErrors = validateRow(row, mapping);
        if (rowErrors.length > 0) {
          newErrors.push({
            row: index + 1,
            reason: rowErrors.join(', '),
            data: row
          });
        }
      });

      setErrors(newErrors);
      setDuplicates(newDuplicates);
      setIsProcessing(false);
    };

    processData();
  }, [rows, mapping]);

  const validateRow = (row: CsvRow, mapping: Record<string, string>): string[] => {
    const errors: string[] = [];
    
    // Check required fields
    const requiredFields = [
      { key: 'display_name', label: 'Display Name' },
      { key: 'billing_addr_line1', label: 'Street Address' },
      { key: 'billing_addr_city', label: 'City' },
      { key: 'billing_addr_state', label: 'State' },
      { key: 'billing_addr_zip', label: 'ZIP Code' },
    ];

    requiredFields.forEach(field => {
      const csvHeader = mapping[field.key];
      if (!csvHeader || !row[csvHeader]?.trim()) {
        errors.push(`Missing ${field.label}`);
      }
    });

    // Check contact information (email OR phone required)
    const emailHeader = mapping.email;
    const phoneHeader = mapping.phone;
    const hasEmail = emailHeader && row[emailHeader]?.trim();
    const hasPhone = phoneHeader && row[phoneHeader]?.trim();
    
    if (!hasEmail && !hasPhone) {
      errors.push('Email or Phone required');
    }

    // Email format validation
    if (hasEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row[emailHeader!])) {
      errors.push('Invalid email format');
    }

    return errors;
  };

  const handleNext = useCallback(() => {
    onComplete(errors, duplicates);
  }, [errors, duplicates, onComplete]);

  const getRowError = (rowIndex: number): RowError | undefined => {
    return errors.find(error => error.row === rowIndex + 1);
  };

  const getCellValue = (row: CsvRow, fieldKey: string): string => {
    const csvHeader = mapping[fieldKey];
    return csvHeader ? row[csvHeader] || '' : '';
  };

  const displayFields = [
    { key: 'display_name', label: 'Display Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'billing_addr_line1', label: 'Address' },
    { key: 'billing_addr_city', label: 'City' },
    { key: 'billing_addr_state', label: 'State' },
    { key: 'billing_addr_zip', label: 'ZIP' },
  ];

  if (isProcessing) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 dark:text-neutral-400">
            Validating CSV data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Preview Data
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Review your data before importing. Rows with errors are highlighted in red.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center">
              <FiCheck className="h-5 w-5 text-green-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  Valid Rows
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {rows.length - errors.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center">
              <FiAlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Errors
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {errors.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center">
              <FiAlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
              <div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Duplicates
                </p>
                <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {duplicates.length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="h-5 w-5 bg-blue-500 rounded mr-2"></div>
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  Total Rows
                </p>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {rows.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Preview Table */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
              <thead className="bg-neutral-50 dark:bg-neutral-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                    Row
                  </th>
                  {displayFields.map(field => (
                    <th key={field.key} className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                      {field.label}
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-300 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                {rows.slice(0, 10).map((row, index) => {
                  const rowError = getRowError(index);
                  const hasError = !!rowError;
                  
                  return (
                    <tr key={index} className={hasError ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-900 dark:text-neutral-50">
                        {index + 1}
                      </td>
                      {displayFields.map(field => (
                        <td key={field.key} className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900 dark:text-neutral-50">
                          <span className="truncate max-w-xs block" title={getCellValue(row, field.key)}>
                            {getCellValue(row, field.key)}
                          </span>
                        </td>
                      ))}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {hasError ? (
                          <div className="flex items-center text-red-600 dark:text-red-400">
                            <FiAlertCircle className="h-4 w-4 mr-1" />
                            <span className="text-xs">{rowError.reason}</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-green-600 dark:text-green-400">
                            <FiCheck className="h-4 w-4 mr-1" />
                            <span className="text-xs">Valid</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {rows.length > 10 && (
          <div className="mt-4 text-center text-sm text-neutral-600 dark:text-neutral-400">
            Showing first 10 rows of {rows.length} total rows
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            <FiArrowLeft className="mr-2" size={16} />
            Back
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Next
              <FiArrowRight className="ml-2" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 